import React from 'react';
import { Box, Typography, Button } from '@mui/material';

// A ChunkLoadError almost always means a new version was deployed and the old
// hashed chunk this tab still references no longer exists. Recognise it so we
// can silently reload to the fresh build instead of showing an error screen.
const isChunkLoadError = error => {
  if (!error) return false;
  const name = error.name ?? '';
  const message = error.message ?? '';
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [^\s]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
};

// Guard against a reload loop: if reloading doesn't fix it (chunk genuinely
// gone, or offline), don't keep refreshing — fall back to the error UI.
const CHUNK_RELOAD_KEY = 'chunkReloadAt';
const CHUNK_RELOAD_WINDOW_MS = 10000;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorInfo: null,
      error: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Flag chunk errors so render() shows a blank frame (no error flash) while
    // the reload in componentDidCatch kicks in.
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error)) {
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
      const now = Date.now();
      // Only auto-reload if we haven't just done so — otherwise we'd loop.
      if (now - last > CHUNK_RELOAD_WINDOW_MS) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
        window.location.reload();
        return;
      }
      // Reload already attempted and it still failed → show the error screen.
      this.setState({ isChunkError: false, error, errorInfo });
      return;
    }
    this.setState({ errorInfo, error });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    // Chunk error → we're reloading; render nothing to avoid an error flash.
    if (this.state.hasError && this.state.isChunkError) {
      return null;
    }

    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3,
            textAlign: 'center',
            backgroundColor: '#f8fafc',
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ color: '#dc2626' }}
          >
            Oops! Something went wrong
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, color: '#6b7280' }}>
            {this.state.error?.name === 'ChunkLoadError'
              ? 'A new version may have been deployed or the page failed to load. Please refresh to get the latest version.'
              : "We're sorry, but something unexpected happened. Please try refreshing the page."}
          </Typography>

          <Button
            variant="contained"
            onClick={this.handleReload}
            sx={{
              backgroundColor: '#dc2626',
              '&:hover': {
                backgroundColor: '#b91c1c',
              },
            }}
          >
            Refresh Page
          </Button>

          {process.env.NODE_ENV === 'development' && (
            <Box
              sx={{
                mt: 4,
                p: 2,
                backgroundColor: '#fef2f2',
                borderRadius: 1,
                maxWidth: '100%',
                overflow: 'auto',
              }}
            >
              <Typography variant="h6" sx={{ color: '#dc2626', mb: 1 }}>
                Error Details (Development Only):
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  color: '#6b7280',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {this.state.errorInfo?.componentStack ||
                  'No error details available'}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
